import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteMobileNotificationCampaign,
  duplicateMobileNotificationCampaign,
  getMobileNotificationCampaign,
  listMobileNotificationDeliveries,
  mapLaravelError,
} from '../../services/mobileNotificationCampaignsApi';
import MobileNotificationDeliveriesTable from './components/MobileNotificationDeliveriesTable';
import MobileNotificationStatusChip from './components/MobileNotificationStatusChip';
import recurrenceSummary from './utils/recurrenceSummary';

const unwrapCampaign = (payload) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload) && payload.id != null) {
    return payload;
  }

  if (payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && payload.data.id != null) {
    return payload.data;
  }

  return payload || null;
};

export default function MobileNotificationDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const campaignId = Number(id || 0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaign, setCampaign] = useState(null);
  const [tab, setTab] = useState('deliveries');
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryMeta, setDeliveryMeta] = useState({ current_page: 1, per_page: 25, total: 0 });

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await getMobileNotificationCampaign(campaignId);
      setCampaign(unwrapCampaign(payload));
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const loadDeliveries = useCallback(async (page = 1, perPage = 25) => {
    setDeliveriesLoading(true);
    try {
      const payload = await listMobileNotificationDeliveries(campaignId, {
        page,
        per_page: perPage,
      });
      setDeliveries(Array.isArray(payload?.data) ? payload.data : []);
      setDeliveryMeta(payload?.meta || { current_page: page, per_page: perPage, total: 0 });
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setDeliveriesLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    if (tab === 'deliveries') {
      loadDeliveries(deliveryMeta.current_page || 1, deliveryMeta.per_page || 25);
    }
  }, [deliveryMeta.current_page, deliveryMeta.per_page, loadDeliveries, tab]);

  const readOnly = useMemo(
    () => ['sending', 'sent', 'cancelled'].includes(String(campaign?.status || '').toLowerCase()),
    [campaign?.status]
  );

  const handleDuplicate = async () => {
    try {
      const payload = await duplicateMobileNotificationCampaign(campaignId);
      const nextId = payload?.campaign?.id || payload?.campaign?.data?.id;
      if (nextId) {
        navigate(`/admin/communications/mobile-notifications/${nextId}/edit`);
      }
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(`Delete "${campaign?.title || 'this campaign'}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteMobileNotificationCampaign(campaignId);
      navigate('/admin/communications/mobile-notifications');
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Campaign not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.75}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {campaign.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <MobileNotificationStatusChip status={campaign.status} />
              <Typography variant="body2" color="text.secondary">
                {Number(campaign.recipient_snapshot_count || 0)} recipients
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button onClick={handleDuplicate}>
              Duplicate
            </Button>
            <Button
              onClick={() => navigate(`/admin/communications/mobile-notifications/${campaign.id}/edit`)}
              disabled={readOnly}
            >
              Edit
            </Button>
            <Button color="error" onClick={handleDelete} disabled={!campaign.can_delete}>
              Delete
            </Button>
            <Button onClick={() => navigate('/admin/communications/mobile-notifications')}>Back</Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Alert severity="info">
          This campaign is mobile-only. Web users will not see these inbox records in the notification center or header.
        </Alert>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Schedule</Typography>
            <Typography variant="body2">{recurrenceSummary(campaign)}</Typography>

            <Divider />

            <Typography variant="subtitle2">Delivery Stats</Typography>
            <Typography variant="body2">
              Sent: {Number(campaign?.delivery_stats?.sent || 0)} | Inbox only: {Number(campaign?.delivery_stats?.inbox_only || 0)} | Failed: {Number(campaign?.delivery_stats?.failed || 0)} | Queued: {Number(campaign?.delivery_stats?.queued || 0)}
            </Typography>
          </Stack>
        </Paper>

        <Paper variant="outlined">
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab value="deliveries" label="Deliveries" />
            <Tab value="content" label="Content" />
            <Tab value="settings" label="Settings" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {tab === 'deliveries' ? (
              <MobileNotificationDeliveriesTable
                rows={deliveries}
                loading={deliveriesLoading}
                page={deliveryMeta.current_page || 1}
                perPage={deliveryMeta.per_page || 25}
                total={deliveryMeta.total || 0}
                onPageChange={(nextPage) => setDeliveryMeta((prev) => ({ ...prev, current_page: nextPage }))}
                onPerPageChange={(nextPerPage) => setDeliveryMeta((prev) => ({ ...prev, per_page: nextPerPage, current_page: 1 }))}
              />
            ) : null}

            {tab === 'content' ? (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Message</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {campaign.message || '-'}
                </Typography>

                <Divider />

                <Typography variant="subtitle2">Metadata</Typography>
                <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                  {campaign.data ? JSON.stringify(campaign.data, null, 2) : 'No metadata'}
                </Typography>
              </Stack>
            ) : null}

            {tab === 'settings' ? (
              <Stack spacing={1.2}>
                <Typography variant="subtitle2">Audience</Typography>
                <Typography variant="body2">{campaign.audience_summary}</Typography>

                <Typography variant="subtitle2">Timezone</Typography>
                <Typography variant="body2">{campaign.timezone || '-'}</Typography>

                <Typography variant="subtitle2">Occurrences Sent</Typography>
                <Typography variant="body2">{Number(campaign.occurrences_sent || 0)}</Typography>
              </Stack>
            ) : null}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
